import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import { FaSlidersH, FaTimes, FaSave } from 'react-icons/fa';
import * as Yup from 'yup';

import { useAuth } from '~/hooks';

import api from '~/services/api';
import history from '~/services/history';

import { Input, FormLoading, FormContainer } from '~/components/Form';
import Loading from '~/components/Loading';
import ConfirmWindow from '~/components/ConfirmWindow';

import { Container, Header, Controls, Content, ParameterInfo } from './styles';

const Edit = () => {
  const { company } = useAuth();

  const { state } = useLocation();

  const id = state?.id || null;

  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [parameter, setParameter] = useState(null);

  useEffect(() => {
    async function loadParameter() {
      if (id) {
        try {
          setLoading(true);

          const response = await api.get(`checklists-parameters/${id}`);

          if (response.data) {
            const formattedParameter = {
              id: response.data.id,
              client: response.data.client,
              model: response.data.model,
              start_period: response.data.start_period
                ? format(parseISO(response.data.start_period), 'dd/MM/yyyy')
                : null,
              end_period: response.data.end_period
                ? format(parseISO(response.data.end_period), 'dd/MM/yyyy')
                : null,
              observation: response.data.obs,
            };

            setParameter(formattedParameter);
          }

          setLoading(false);
        } catch {
          toast.error('Falha ao buscar parâmetro.', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });

          setLoading(false);
        }
      }
    }

    loadParameter();
  }, [id]);

  const handleSubmit = useCallback(
    async data => {
      if (company && id) {
        try {
          setSaveLoading(true);

          const schema = Yup.object().shape({
            observation: Yup.string().nullable(),
          });

          await schema.validate(data, {
            abortEarly: false,
          });

          await api.put(`checklists-parameters/${id}`, {
            obs: data.observation,
          });

          formRef.current.setErrors({});

          toast.success('Observação salva com sucesso.', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });

          setSaveLoading(false);

          history.push('/observation');
        } catch (err) {
          if (err instanceof Yup.ValidationError) {
            const errorMessages = {};

            err.inner.forEach(error => {
              errorMessages[error.path] = error.message;
            });

            formRef.current.setErrors(errorMessages);
          } else {
            toast.error('Falha ao salvar observação.', {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }

          setSaveLoading(false);
        }
      }
    },
    [company, id]
  );

  const handleClose = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={() => history.push('/observation')}
            onClose={onClose}
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, []);

  return (
    <>
      <Container>
        <Header>
          <div>
            <FaSlidersH size={20} color="#44546a" />
            <h1>Editar Observação Tributária</h1>
          </div>
        </Header>
        <Controls>
          <button type="button" onClick={() => formRef.current.submitForm()}>
            <FaSave />
            <span>Salvar</span>
          </button>
          <button type="button" onClick={handleClose}>
            <FaTimes />
            <span>Fechar</span>
          </button>
        </Controls>
        {(loading || !company) && <FormLoading className="loading" />}
        {parameter && (
          <Content className="content">
            <FormContainer
              ref={formRef}
              loading={loading ? 1 : 0}
              onSubmit={handleSubmit}
              initialData={parameter}
            >
              <ParameterInfo>
                <h4>PARÂMETRO</h4>
                <section>
                  <Input
                    name="client.name"
                    className="client"
                    label="Empresa"
                    readOnly
                  />
                  <Input
                    name="model.name"
                    className="model"
                    label="Modelo"
                    readOnly
                  />
                  <Input
                    name="start_period"
                    className="start_period"
                    label="Início"
                    readOnly
                  />
                  <Input
                    name="end_period"
                    className="end_period"
                    label="Fim"
                    readOnly
                  />
                </section>
                <section>
                  <Input
                    name="observation"
                    className="observation"
                    label="Observações"
                  />
                </section>
              </ParameterInfo>
            </FormContainer>
          </Content>
        )}
      </Container>

      {saveLoading && <Loading />}
    </>
  );
};

export default Edit;
